import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from '../../../components/Button/Button';
import { _getStakingCoins } from '../redux/actions';
import { setStakingCoins, setStakingCoinsLoading, setStakingCoinsError } from '../redux/slices/stakingSlice';
import Styles from './stakingCalculatorPage.module.css';

const StakingCalculatorPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Redux selectors
    const { stakingCoins, stakingCoinsLoading } = useSelector(state => ({
        stakingCoins: state.staking.coins.data,
        stakingCoinsLoading: state.staking.coins.loading
    }));

    const [calculatorInputs, setCalculatorInputs] = useState({
        coin: '',
        amount: '',
        duration: '',
        apy: '',
        compounding: 'daily'
    });

    // Fetch staking coins on component mount
    useEffect(() => {
        const fetchStakingCoins = () => {
            dispatch(setStakingCoinsLoading(true));

            _getStakingCoins(
                {}, // Use default params (enabled coins only)
                (response) => {
                    console.log('Staking coins response:', response);
                    dispatch(setStakingCoins(response));

                    // Set first coin as default if available
                    if (response && response.length > 0) {
                        const firstCoin = response[0];
                        setCalculatorInputs(prev => ({
                            ...prev,
                            coin: firstCoin.currency_id.toUpperCase()
                        }));
                    }
                },
                (error) => {
                    console.error('Error fetching staking coins:', error);
                    dispatch(setStakingCoinsError(error.message || 'Failed to fetch staking coins'));
                }
            );
        };

        fetchStakingCoins();
    }, [dispatch]);

    // Helper functions
    const getCoinName = (symbol) => {
        const coinNames = {
            'btc': 'Bitcoin',
            'eth': 'Ethereum',
            'ada': 'Cardano',
            'sol': 'Solana',
            'dot': 'Polkadot',
            'usdt': 'Tether USD',
            'link': 'Chainlink',
            'avax': 'Avalanche',
            'flr': 'Flare',
            'trx': 'Tron'
        };
        return coinNames[symbol.toLowerCase()] || symbol.toUpperCase();
    };

    const getDefaultColor = (symbol) => {
        const colors = {
            'btc': '#f7931a',
            'eth': '#627eea',
            'ada': '#0033ad',
            'sol': '#9945ff',
            'dot': '#e6007a',
            'usdt': '#26a17b',
            'link': '#375bd2',
            'avax': '#e84142',
            'flr': '#1f2937',
            'trx': '#ff060a'
        };
        return colors[symbol.toLowerCase()] || '#6b7280';
    };

    // Transform API data to match the existing coin structure
    const coins = stakingCoins.map(coin => ({
        symbol: coin.currency_id.toUpperCase(),
        name: getCoinName(coin.currency_id),
        color: getDefaultColor(coin.currency_id),
        iconUrl: coin.icon_url,
        availableDurations: coin.available_durations,
        status: coin.status,
        apyRange: [5.0, 15.0] // Default range, could be enhanced with actual APY data
    }));

    // Get available durations for selected coin
    const getAvailableDurations = () => {
        const selectedCoin = coins.find(coin => coin.symbol === calculatorInputs.coin);
        if (selectedCoin && selectedCoin.availableDurations) {
            return [...selectedCoin.availableDurations].sort((a, b) => a - b);
        }
        return [30, 60, 90, 365]; // Fallback durations
    };

    // Format duration display
    const formatDuration = (duration) => {
        if (duration === 0) return 'Flexible';
        if (duration >= 365) return `${Math.floor(duration / 365)}y`;
        return `${duration}d`;
    };

    const compoundingFrequency = {
        daily: 365,
        weekly: 52,
        monthly: 12,
        quarterly: 4,
        annually: 1
    };

    const formatNumber = (num, decimals = 6) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num);
    };

    const calculateCompoundReturns = (principal, rate, time, compounds) => {
        const compoundRate = rate / 100 / compounds;
        const compoundTime = compounds * (time / 365);
        return principal * Math.pow(1 + compoundRate, compoundTime);
    };

    const calculateSimpleReturns = (principal, rate, time) => {
        return principal + (principal * (rate / 100) * (time / 365));
    };

    const getSelectedCoin = (symbol) => {
        return coins.find(coin => coin.symbol === symbol);
    };

    const calculatorResults = () => {
        const principal = parseFloat(calculatorInputs.amount) || 0;
        const rate = parseFloat(calculatorInputs.apy) || 0;
        const time = parseFloat(calculatorInputs.duration) || 0;
        const compounds = compoundingFrequency[calculatorInputs.compounding] || 365;

        const compoundAmount = calculateCompoundReturns(principal, rate, time, compounds);
        const simpleAmount = calculateSimpleReturns(principal, rate, time);

        return {
            principal,
            compoundAmount,
            simpleAmount,
            compoundReturns: compoundAmount - principal,
            simpleReturns: simpleAmount - principal,
            difference: compoundAmount - simpleAmount
        };
    };

    const results = calculatorResults();

    return (
        <div className={Styles.calculatorContainer}>
            {/* Header Section */}
            <div className={Styles.headerSection}>
                <h1 className={Styles.headerTitle}>
                    Staking Calculator
                </h1>
                <p className={Styles.headerDescription}>
                    Calculate your potential staking rewards with compound interest for different cryptocurrencies
                </p>
            </div>

            {/* Disclaimer Section */} 
            <div className={Styles.disclaimerSection}>
                <div className={Styles.disclaimerContent}>
                    <h3 className={Styles.disclaimerTitle}>
                        <FiAlertTriangle size={20} />
                        Important Disclaimer
                    </h3>
                    <p className={Styles.disclaimerText}>
                        <strong>Disclaimer:</strong> This feature is provided for illustrative and informational purposes only.
                        The figures shown are estimates based on publicly available data and do not represent actual staking rewards.
                        Actual rewards may vary depending on market conditions, validator performance, and other factors.
                        This tool does not facilitate staking or provide financial advice.
                    </p>
                </div>
            </div>

            <div className={Styles.mainContent}>
                {/* Calculator Form */}
                <div className={Styles.calculatorForm}>
                    <h2 className={Styles.formTitle}>
                        Calculator Settings
                    </h2>

                    {/* Cryptocurrency Selection */}
                    <div className={Styles.inputGroup}>
                        <label className={Styles.inputLabel}>
                            Select Cryptocurrency
                        </label>
                        <select
                            value={calculatorInputs.coin}
                            onChange={(e) => {
                                const selectedCoin = getSelectedCoin(e.target.value);
                                setCalculatorInputs({
                                    ...calculatorInputs,
                                    coin: e.target.value,
                                    apy: selectedCoin.apyRange[1].toString()
                                });
                            }}
                            className={Styles.selectInput}
                        >
                            {coins.map(coin => (
                                <option key={coin.symbol} value={coin.symbol}>
                                    {coin.name} ({coin.symbol})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Stake Amount */}
                    <div className={Styles.inputGroup}>
                        <label className={Styles.inputLabel}>
                            Stake Amount ({calculatorInputs.coin})
                        </label>
                        <input
                            type="number"
                            value={calculatorInputs.amount}
                            onChange={(e) => setCalculatorInputs({ ...calculatorInputs, amount: e.target.value })}
                            placeholder="Enter amount"
                            className={Styles.numberInput}
                        />
                    </div>

                    {/* APY */}
                    <div className={Styles.inputGroup}>
                        <label className={Styles.inputLabel}>
                            Annual Percentage Yield (%)
                        </label>
                        <input
                            type="number"
                            value={calculatorInputs.apy}
                            onChange={(e) => setCalculatorInputs({ ...calculatorInputs, apy: e.target.value })}
                            placeholder="Enter APY"
                            step="0.1"
                            className={Styles.numberInput}
                        />
                        <div className={Styles.apyRange}>
                            Range: {getSelectedCoin(calculatorInputs.coin)?.apyRange[0]}% - {getSelectedCoin(calculatorInputs.coin)?.apyRange[1]}%
                        </div>
                    </div>

                    {/* Duration */}
                    <div className={Styles.inputGroup}>
                        <label className={Styles.inputLabel}>
                            Staking Duration (Days)
                        </label>
                        <div className={Styles.durationButtons}>
                            {getAvailableDurations().map(days => (
                                <button
                                    key={days}
                                    onClick={() => setCalculatorInputs({ ...calculatorInputs, duration: days.toString() })}
                                    className={`${Styles.durationButton} ${calculatorInputs.duration === days.toString() ? Styles.durationButtonActive : ''
                                        }`}
                                >
                                    {formatDuration(days)}
                                </button>
                            ))}
                        </div>
                    </div>


                    {/* Reset Button */}
                    <Button
                        text="Reset Calculator"
                        onClick={() => setCalculatorInputs({
                            coin: 'BTC',
                            amount: '',
                            duration: '60',
                            apy: '10.2',
                            compounding: 'daily'
                        })}
                        className={Styles.resetButton}
                    />
                </div>

                {/* Results Panel */}
                <div className={Styles.resultsPanel}>
                    <h2 className={Styles.resultsTitle}>
                        Staking Results
                    </h2>

                    {/* Principal Investment */}
                    <div className={Styles.resultCard}>
                        <div className={Styles.resultLabel}>
                            Initial Investment
                        </div>
                        <div className={Styles.resultValue}>
                            {formatNumber(results.principal, 6)} {calculatorInputs.coin}
                        </div>
                    </div>

                    {/* Compound Returns */}
                    <div className={`${Styles.resultCard} ${Styles.compoundCard}`}>
                        <div className={Styles.resultLabel}>
                            With Compound Interest
                        </div>
                        <div className={Styles.resultValue}>
                            {formatNumber(results.compoundAmount, 6)} {calculatorInputs.coin}
                        </div>
                        <div className={Styles.resultEarnings}>
                            +{formatNumber(results.compoundReturns, 6)} {calculatorInputs.coin} earned
                        </div>
                    </div>

                    {/* Simple Returns */}
                    <div className={`${Styles.resultCard} ${Styles.simpleCard}`}>
                        <div className={Styles.resultLabel}>
                            Simple Interest (No Compounding)
                        </div>
                        <div className={Styles.resultValue}>
                            {formatNumber(results.simpleAmount, 6)} {calculatorInputs.coin}
                        </div>
                        <div className={Styles.resultEarnings}>
                            +{formatNumber(results.simpleReturns, 6)} {calculatorInputs.coin} earned
                        </div>
                    </div>

                    {/* Compound Advantage */}
                    <div className={`${Styles.resultCard} ${Styles.advantageCard}`}>
                        <div className={Styles.resultLabel}>
                            Compound Advantage
                        </div>
                        <div className={Styles.resultValue}>
                            +{formatNumber(results.difference, 6)} {calculatorInputs.coin}
                        </div>
                        <div className={Styles.resultDescription}>
                            Extra earnings from compounding
                        </div>
                    </div>

                    {/* Breakdown */}
                    <div className={Styles.breakdownCard}>
                        <h3 className={Styles.breakdownTitle}>
                            Breakdown
                        </h3>

                        <div className={Styles.breakdownList}>
                            <div className={Styles.breakdownRow}>
                                <span className={Styles.breakdownLabel}>Staking Period:</span>
                                <span className={Styles.breakdownValue}>{calculatorInputs.duration} days</span>
                            </div>
                            <div className={Styles.breakdownRow}>
                                <span className={Styles.breakdownLabel}>APY Rate:</span>
                                <span className={Styles.breakdownValue}>{calculatorInputs.apy}%</span>
                            </div>
                            <div className={Styles.breakdownRow}>
                                <span className={Styles.breakdownLabel}>Compounding:</span>
                                <span className={Styles.breakdownValue}>{calculatorInputs.compounding}</span>
                            </div>
                            <div className={Styles.breakdownDivider}></div>
                            <div className={Styles.breakdownRow}>
                                <span className={Styles.breakdownLabel}>Effective Return:</span>
                                <span className={Styles.breakdownValue}>
                                    {results.principal > 0 ? ((results.compoundReturns / results.principal) * 100).toFixed(2) : 0}%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Educational Info */}
            <div className={Styles.educationalSection}>
                <div className={Styles.educationalContent}>
                    <h3 className={Styles.educationalTitle}>
                        How Compound Interest Works
                    </h3>
                    <p className={Styles.educationalText}>
                        Compound interest means earning returns not just on your initial stake, but also on the rewards you've already earned.
                        The more frequently rewards are compounded, the more you earn over time. This calculator shows the power of
                        compound growth in cryptocurrency staking.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default StakingCalculatorPage;
